import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from '@mui/material';
import { ProgressContext } from './ModulePageLayout';
import {
  workshopRatingAPI,
  PendingWorkshopRating,
} from '../services/api';
import { getLocalizedText, getDisplayLocale } from '../utils/i18n';
import { getLearnerFacingApiError } from '../utils/learnerErrors';
import '../styles/components/_chapter-rating.scss';
import '../styles/components/_workshop-rating-modal.scss';

type Step = 'rating' | 'comment';

/** Texte lisible sur fond blanc (thème app en dark → éviter blanc sur blanc dans la popup) */
const DIALOG_INK = '#13322B';
const DIALOG_MUTED = 'rgba(41, 55, 48, 0.65)';
const DIALOG_BORDER = 'rgba(41, 55, 48, 0.35)';

function capitalizeFirstLetter(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatWorkshopScheduleLine(
  lang: string,
  iso?: string | null,
  start?: string | null,
  end?: string | null
): string {
  const locale = getDisplayLocale(lang);
  const parts: string[] = [];
  if (iso) {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) {
      parts.push(
        capitalizeFirstLetter(
          d.toLocaleDateString(locale, {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })
        )
      );
    }
  }
  if (start && end) {
    parts.push(`${start} – ${end}`);
  } else if (start) {
    parts.push(start);
  }
  return parts.join(' · ');
}

const WorkshopRatingGate: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const { t, i18n } = useTranslation();
  const progressCtx = useContext(ProgressContext);

  const [pending, setPending] = useState<PendingWorkshopRating[]>([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<Step>('rating');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const current = pending[0] ?? null;

  const loadPending = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!courseId) return;
      try {
        if (!opts?.silent) {
          setLoading(true);
          setError(null);
        }
        const data = await workshopRatingAPI.getPending(courseId);
        setPending(data.pending || []);
      } catch (e: unknown) {
        console.error(e);
        setError(getLearnerFacingApiError(e, t));
        setPending([]);
      } finally {
        if (!opts?.silent) setLoading(false);
      }
    },
    [courseId, t]
  );

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  useEffect(() => {
    setStep('rating');
    setRating(0);
    setComment('');
    setError(null);
  }, [current?.chapterId, current?.workshopItemId]);

  const handleAbsent = async () => {
    if (!courseId || !current) return;
    try {
      setSaving(true);
      setError(null);
      await workshopRatingAPI.save({
        courseId,
        chapterId: current.chapterId,
        workshopItemId: current.workshopItemId,
        status: 'absent',
      });
      await progressCtx?.refreshCourse?.().catch(() => {});
      await loadPending({ silent: true });
    } catch (e: unknown) {
      setError(getLearnerFacingApiError(e, t));
    } finally {
      setSaving(false);
    }
  };

  const handleNextToComment = () => {
    if (!rating) return;
    setStep('comment');
  };

  const handleSubmitRated = async () => {
    if (!courseId || !current || !rating) return;
    try {
      setSaving(true);
      setError(null);
      await workshopRatingAPI.save({
        courseId,
        chapterId: current.chapterId,
        workshopItemId: current.workshopItemId,
        status: 'rated',
        rating,
        comment: comment.trim() || undefined,
      });
      await progressCtx?.refreshCourse?.().catch(() => {});
      await loadPending({ silent: true });
    } catch (e: unknown) {
      setError(getLearnerFacingApiError(e, t));
    } finally {
      setSaving(false);
    }
  };

  /** La dialog ne s’ouvre qu’après la réponse API : jamais pendant le chargement ni si la liste est vide. */
  const dialogOpen = Boolean(courseId && !loading && pending.length > 0);

  const workshopTitle = current ? getLocalizedText(current.workshopTitle) : '';
  const chapterTitle = current ? getLocalizedText(current.chapterTitle) : '';

  const scheduleLine = useMemo(() => {
    if (!current) return '';
    const raw = formatWorkshopScheduleLine(
      i18n.language,
      current.workshopDate,
      current.startTime,
      current.endTime
    );
    return raw.trim() || t('workshopRating.noSchedule');
  }, [current, i18n.language, t]);

  return (
    <Dialog
      open={dialogOpen}
      disableEscapeKeyDown
      onClose={() => {}}
      maxWidth={false}
      fullWidth={false}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: '24px',
          backgroundColor: '#fff',
          maxWidth: { xs: 'calc(100vw - 24px)', sm: 720 },
          width: '100%',
          m: 2,
          color: DIALOG_INK,
        },
      }}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0,0,0,0.6)' },
        },
      }}
    >
      <DialogContent
        sx={{
          p: { xs: 2, sm: 3 },
          overflow: 'visible',
          '&.MuiDialogContent-root': { overflow: 'visible' },
        }}
      >
        {current && (
          <Box sx={{ maxWidth: '100%' }}>
            <Box className="chapter-rating__card" sx={{ boxShadow: 'none', p: { xs: 1.5, sm: 2 }, maxWidth: '100%' }}>
              <Box className="workshop-rating-modal__title-block">
                <Box className="chapter-rating__header-section" sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, mb: 1 }}>
                  <Typography variant="overline" sx={{ display: 'block', color: DIALOG_MUTED, letterSpacing: 0.5 }}>
                    {t('workshopRating.modalTitle')}
                  </Typography>
                  <p className="workshop-rating-modal__workshop-title">{workshopTitle || '—'}</p>
                  <p className="workshop-rating-modal__meta">{scheduleLine}</p>
                  {chapterTitle ? (
                    <p className="workshop-rating-modal__chapter-hint">{chapterTitle}</p>
                  ) : null}
                </Box>
              </Box>

              {error && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="error">
                    {error}
                  </Typography>
                </Box>
              )}

              {step === 'rating' && (
                <>
                  <Box className="chapter-rating__question-section">
                    <p className="chapter-rating__question">{t('workshopRating.questionRate')}</p>
                  </Box>

                  <Box className="chapter-rating__rating-section">
                    <Box className="chapter-rating__stars-container">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((starValue) => {
                        const isSelected = rating >= starValue;
                        let starColor = '#ff4d4d';
                        if (starValue >= 9) starColor = '#4caf50';
                        else if (starValue >= 7) starColor = '#ffc107';

                        return (
                          <Box
                            key={starValue}
                            className={`chapter-rating__star-wrapper ${isSelected ? 'chapter-rating__star-wrapper--selected' : ''}`}
                            onClick={() => !saving && setRating(starValue)}
                            style={{ cursor: saving ? 'default' : 'pointer' }}
                          >
                            <Box
                              className="chapter-rating__star"
                              sx={{
                                borderColor: starColor,
                                backgroundColor: isSelected ? starColor : 'transparent',
                              }}
                            >
                              <Typography
                                variant="body2"
                                className="chapter-rating__star-number"
                                sx={{
                                  color: isSelected ? '#fff' : starColor,
                                  fontWeight: 700,
                                }}
                              >
                                {starValue}
                              </Typography>
                            </Box>
                            {starValue === 1 && (
                              <Typography
                                variant="caption"
                                className="chapter-rating__star-label chapter-rating__star-label--left"
                              >
                                {t('chapterRating.labels.nul')}
                              </Typography>
                            )}
                            {starValue === 10 && (
                              <Typography
                                variant="caption"
                                className="chapter-rating__star-label chapter-rating__star-label--right"
                              >
                                {t('chapterRating.labels.top')}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      mt: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                      alignItems: 'stretch',
                    }}
                  >
                    <Button
                      variant="contained"
                      disabled={saving || !rating}
                      onClick={handleNextToComment}
                      className="button-primary"
                      fullWidth
                      sx={{ minHeight: 44 }}
                    >
                      {t('workshopRating.next')}
                    </Button>
                    <Button
                      variant="outlined"
                      disabled={saving}
                      onClick={handleAbsent}
                      fullWidth
                      className="button-secondary-outlined"
                      sx={{
                        minHeight: 44,
                        color: DIALOG_INK,
                        borderColor: DIALOG_BORDER,
                        backgroundColor: '#fff',
                        '&:hover': {
                          borderColor: DIALOG_INK,
                          backgroundColor: 'rgba(41, 55, 48, 0.06)',
                        },
                        '&.Mui-disabled': {
                          color: 'rgba(41, 55, 48, 0.35)',
                          borderColor: 'rgba(41, 55, 48, 0.2)',
                        },
                      }}
                    >
                      {t('workshopRating.absent')}
                    </Button>
                  </Box>
                </>
              )}

              {step === 'comment' && (
                <>
                  <Typography variant="body2" sx={{ mb: 1.5, color: DIALOG_INK, lineHeight: 1.5 }}>
                    {t('workshopRating.commentPrompt')}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    variant="outlined"
                    minRows={3}
                    maxRows={8}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder={t('workshopRating.commentPlaceholder')}
                    disabled={saving}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                        '& fieldset': {
                          borderWidth: 1,
                          borderColor: DIALOG_BORDER,
                        },
                        '&:hover fieldset': {
                          borderColor: DIALOG_INK,
                        },
                        '&.Mui-focused fieldset': {
                          borderWidth: 2,
                          borderColor: DIALOG_INK,
                        },
                      },
                      '& .MuiInputBase-input': { color: DIALOG_INK },
                      '& .MuiInputBase-input::placeholder': { color: DIALOG_MUTED, opacity: 1 },
                    }}
                  />
                  <Button
                    variant="contained"
                    className="button-primary"
                    disabled={saving}
                    onClick={handleSubmitRated}
                    fullWidth
                    sx={{ minHeight: 44 }}
                  >
                    {saving ? <CircularProgress size={22} color="inherit" /> : t('workshopRating.save')}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WorkshopRatingGate;
